import React, { useState, useEffect, useRef } from 'react';
import {
  Award,
  FileText,
  CheckCircle,
  ArrowRight,
  Building,
  Users,
  Calculator,
  Trophy,
  Briefcase,
  GraduationCap,
  ChevronRight,
  Download
} from 'lucide-react';

const ProfileDemo = ({ data }) => {
  const [activeSection, setActiveSection] = useState('academics');
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          // Animate completion percentage
          const timer = setTimeout(() => {
            let percent = 0;
            const interval = setInterval(() => {
              percent += 5;
              setCompletionPercentage(percent);
              if (percent >= 85) clearInterval(interval);
            }, 50);
          }, 500);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const profileSections = {
    academics: {
      icon: GraduationCap,
      title: 'Academic Profile',
      items: [
        { label: 'GPA', value: `${data.profile.gpa} / ${data.profile.weightedGPA}W`, complete: true },
        { label: 'SAT Score', value: data.profile.satScore, complete: true },
        { label: 'Class Rank', value: 'Top 5%', complete: true },
        { label: 'AP Courses', value: '8 completed, 5 in progress', complete: true }
      ]
    },
    activities: {
      icon: Users,
      title: 'Leadership & Activities',
      items: data.profile.activities.map(activity => ({
        label: activity.name,
        value: `${activity.years} • ${activity.hoursPerWeek}hrs/week`,
        complete: true
      }))
    },
    achievements: {
      icon: Trophy,
      title: 'Awards & Recognition',
      items: data.profile.awards.map(award => ({
        label: award,
        value: '✓',
        complete: true
      }))
    }
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-50 rounded-full mb-4">
          <Award className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-medium text-violet-700">
            Foundation Building
          </span>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Comprehensive Profile Development
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          We establish a comprehensive profile that serves as the single source of truth.
          This data automatically populates throughout your applications, recommendations, and materials.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Profile Input */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200">
            {/* Tabs */}
            <div className="flex border-b border-slate-200">
              {Object.entries(profileSections).map(([key, section]) => {
                const Icon = section.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveSection(key)}
                    className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 transition-all
                      ${activeSection === key
                        ? 'bg-azure-50 border-b-2 border-azure-600 text-azure-700'
                        : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{section.title}</span>
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                {profileSections[activeSection].items.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg border
                      ${item.complete ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}
                      transition-all duration-500`}
                    style={{
                      opacity: isInView ? 1 : 0,
                      transform: isInView ? 'translateY(0)' : 'translateY(10px)',
                      transitionDelay: `${index * 100}ms`
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {item.complete && (
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      )}
                      <span className="font-medium text-slate-900">{item.label}</span>
                    </div>
                    <span className="text-sm text-slate-600">{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Completion Bar */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Profile Completion</span>
                  <span className="text-sm font-bold text-emerald-600">{completionPercentage}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-1000"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Auto-Population Benefits */}
        <div className="space-y-4">
          {/* Brag Sheet Preview */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-azure-600" />
                Auto-Generated Brag Sheet
              </h3>
              <button className="text-azure-600 hover:text-azure-700">
                <Download className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="font-medium text-slate-900 mb-1">Leadership Experience</div>
                <div className="text-slate-600">
                  • Engineering Club President (2 years)<br />
                  • City Planning Commission Rep<br />
                  • Habitat for Humanity Youth Leader
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="font-medium text-slate-900 mb-1">Key Achievements</div>
                <div className="text-slate-600">
                  • ASCE Bridge Building Champion<br />
                  • State Math Competition Winner<br />
                  • Mayor's Youth Leadership Award
                </div>
              </div>

              <div className="text-xs text-emerald-600 font-medium mt-3">
                ✓ Ready for teacher recommendations
              </div>
            </div>
          </div>

          {/* Data Flow Visualization */}
          <div className="bg-gradient-to-br from-azure-50 to-indigo-50 rounded-xl border border-azure-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">
              One Entry, Multiple Uses
            </h3>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-azure-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  1
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">Enter data once</div>
                  <div className="text-xs text-slate-600">Complete your profile</div>
                </div>
              </div>

              <div className="border-l-2 border-azure-300 ml-4 pl-7 space-y-3">
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-azure-500 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-slate-900">Common App</div>
                    <div className="text-xs text-slate-600">Activities & honors auto-fill</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-azure-500 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-slate-900">Recommendations</div>
                    <div className="text-xs text-slate-600">Brag sheets generated</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-azure-500 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-slate-900">School Forms</div>
                    <div className="text-xs text-slate-600">Transcripts & profiles ready</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-azure-500 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-slate-900">Interviews</div>
                    <div className="text-xs text-slate-600">Talking points prepared</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Time Saved Metric */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600">8 hours</div>
              <div className="text-sm text-slate-600 mt-1">saved on data entry</div>
              <div className="text-xs text-slate-500 mt-3">
                Average student re-enters the same information 12+ times
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDemo;